<!--*
 * Login view to access the application
 *
 * @author   pablo.barco
 * @date     2020-06-29
 *
 * @copyright 2020 Smartmatic Corp. All Rights Reserved.
 *
 * @license
 *
 * Confidential information of Smartmatic Labs. Not for disclosure or
 * distribution prior written consent. This software contains code, techniques
 * and know-how which is confidential and proprietary to Smartmatic.
 *
 * Use of this software is subject to the terms of an end user license
 * agreement.
-->
<template>
  <div class="row no-gutters vh-100">
    <div ref="backgroundContainer" class="col--md--4 offset--md--0 col--sm--8 offset--sm--1 main-page">
      <div class="row additional-info-container">
        <div class="col--12 county-general text-left">
          <div class="row mt-4 no-gutter">
            <div class="col--12 text-left display-3">
              {{ title }}
            </div>
          </div>
        </div>
        <div v-if="date" class="col--12 mt-4">
          <div class="row">
            <div class="col--12 d-flex additional-info align-items-center">
              <div class="d-flex flex-column align-items-start">
                <div>
                  <h5 class="text">{{ $t('login.eventDate.label') }}</h5>
                </div>
                <div><span class="date body-1">{{ stringDate }}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="showDefaultLogo" class="smt-logo">
        <img src="../../assets/images/smartmatic-logo.png"
             srcset="../../assets/images/smartmatic-logo@2x.png 2x, ../../assets/images/smartmatic-logo@3x.png 3x"
             class="Smartmatic-logo">
      </div>
      <div v-else-if="customLogo" class="smt-logo">
        <img :src="customLogo" class="Smartmatic-logo" />
      </div>
    </div>
    <div class="col--md--8 col--sm--12 d-flex justify-content-center align-items-center">
      <div class="d-flex flex-column justify-content-center">
        <div class="change-lang">
          <LanguageButton @onChangeLocale="onChangeLocale" />
        </div>
        <div class="login-form">
          <div>
            <p class="text-uppercase text-xs font-weight-bold mb-2 color-gray-60">
              {{ $t('login.welcome.label').toUpperCase() }}
            </p>
            <h1 class="text-xxl text-gray-80">{{ $t('login.title.label') }}</h1>
            <p class="text-gray-500 font-weight-bold font-size-20">
              {{ $t('login.login.label') }}
            </p>
            <smtt-contextual-box ref="passwordRecoverySuccess" is-visible="false"
                                 :message-title="$t('login.forgotPassword.success.message')"
                                 variation="success" dismissable="true" />
            <LoginForm></LoginForm>
            <div class="mt-4">
              <a class="Forgot-password" href="#" @click="forgotPassword">{{ $t('login.password.forgot.label') }}</a>
            </div>
            <div class="row mt-8">
              <svg v-if="showDefaultLogo" viewBox="0 0 175 20" width="150px" height="20px" fill="#3d3d3d" stroke="none" class="d-sm-block d--md-none mx-auto" style="vertical-align: middle;">
                <path d="M59.567 8.731c-.392-.122-.956-.192-1.663-.196-1.594-.008-2.727-.015-3.2-.022-1.26-.007-2.117-.08-2.548-.227-.267-.076-.46-.203-.548-.365-.068-.113-.145-.36-.145-.884 0-.452.097-.777.285-.968.202-.196.617-.347 1.222-.438.625-.092 1.575-.142 2.84-.142 1.009 0 1.772.055 2.284.161.476.102.786.27.926.497.063.102.14.347.14.906v.265h1.543v-.116c-.011-.65-.072-1.122-.175-1.432-.116-.325-.315-.61-.61-.847-.351-.281-.843-.486-1.461-.604-.618-.114-1.488-.167-2.594-.167-1.193 0-2.237.05-3.106.156-.528.059-.952.144-1.256.25-.303.105-.587.263-.832.466-.525.421-.781 1.097-.781 2.017 0 .818.156 1.447.489 1.866.28.347.679.604 1.183.761.51.154 1.178.23 1.996.23 2.25 0 3.814.006 4.29.018.424.013.76.065.993.15.22.077.37.2.452.386.093.197.142.5.142.907 0 .778-.28 1.216-.853 1.344-.618.142-1.644.21-3.127.21-1.993 0-3.173-.08-3.528-.228-.201-.085-.334-.212-.393-.363-.043-.124-.103-.408-.109-1.073l-.003-.022c-.003-.029-.003-.143-.003-.193v-.115h-1.5l-.002.115c-.007.262-.007.413-.007.438 0 .642.074 1.14.22 1.472.145.34.415.624.8.843.322.175.738.3 1.271.366.526.066 1.336.102 2.423.102 2.062 0 3.505-.084 4.301-.248.825-.168 1.396-.512 1.705-1.02.256-.43.387-1.015.387-1.745 0-.782-.157-1.37-.473-1.787-.254-.34-.575-.577-.975-.724M70.678 12.344L65.791 4.243 63.16 4.243 63.16 14.16 64.622 14.16 64.622 5.701 64.975 5.701 70.091 14.105 70.123 14.16 71.245 14.16 76.34 5.701 76.664 5.701 76.664 14.16 78.128 14.16 78.128 4.243 75.485 4.243 70.678 12.344M82.769 10.737l2.838-5.117h.361l2.8 5.117h-6zm1.915-6.494L79.23 14.16h1.677l1.123-2.05h7.468l1.115 2.05h1.652l-5.443-9.917h-2.138zM102.536 8.758c-.2.13-.458.212-.789.248-.317.032-1.039.05-2.335.05h-4.5V5.62h4.5c1.122 0 1.906.022 2.308.066.374.036.672.124.846.248.264.197.4.643.4 1.333 0 .786-.152 1.305-.43 1.491zm-.052-4.395c-.445-.057-1.447-.097-3.072-.12H93.41v9.917h1.503v-3.723h4.5c1.094 0 1.693.008 1.853.012.192.01.391.033.605.062.537.095.832.416.92.979.03.299.047.847.047 1.626v1.044h1.508v-1.495c0-.814-.028-1.365-.08-1.65-.055-.295-.18-.548-.383-.778-.17-.172-.38-.308-.623-.398.345-.12.609-.297.801-.511.166-.187.28-.432.349-.752.059-.296.09-.735.09-1.298 0-.748-.103-1.344-.314-1.772-.31-.642-.882-1.026-1.701-1.143zM105.469 5.62L110.203 5.62 110.203 14.16 111.705 14.16 111.705 5.62 116.384 5.62 116.384 4.243 105.469 4.243 105.469 5.62M125.495 12.344L120.622 4.243 117.991 4.243 117.991 14.16 119.456 14.16 119.456 5.701 119.806 5.701 124.947 14.16 126.057 14.16 131.142 5.701 131.462 5.701 131.462 14.16 132.928 14.16 132.928 4.243 130.287 4.243 125.495 12.344M137.653 10.737l2.823-5.117h.361l2.782 5.117h-5.966zm1.903-6.494l-5.43 9.917h1.679l1.112-2.05h7.43l1.115 2.05h1.643l-5.417-9.917h-2.132zM145.794 5.62L150.469 5.62 150.469 14.16 151.956 14.16 151.956 5.62 156.576 5.62 156.576 4.243 145.794 4.243 145.794 5.62M158.203 14.16L159.742 14.16 159.742 4.243 158.203 4.243zM172.683 10.689l-.005.118c-.024.761-.059 1.232-.116 1.389-.06.165-.16.292-.309.372-.162.095-.416.162-.763.201-.175.026-.589.051-1.29.075-.623.029-1.177.036-1.645.036-1.35 0-2.41-.036-3.147-.11-.376-.033-.648-.13-.823-.28-.166-.15-.27-.393-.32-.749-.038-.246-.068-.714-.1-1.301-.034-.608-.054-1.129-.054-1.547 0-.843.05-1.508.136-1.974.102-.562.255-.787.365-.874.125-.1.435-.232 1.2-.313.635-.073 1.603-.103 2.87-.103 1.733 0 2.852.092 3.32.283.24.1.388.228.473.396.08.181.124.501.124.956v.1c.003.062.003.14.003.235v.113h1.446v-.116c-.011-.871-.055-1.438-.124-1.724-.076-.332-.259-.626-.545-.874-.274-.243-.608-.416-.989-.505-.732-.161-1.964-.246-3.64-.246-1.75 0-3.102.07-4.03.206-.994.14-1.624.658-1.863 1.544-.195.712-.286 1.863-.286 3.413 0 1.661.123 2.775.381 3.414.264.654.79 1.058 1.559 1.216.771.151 2.265.232 4.436.232 1.666 0 2.854-.067 3.528-.201.858-.173 1.378-.652 1.542-1.42.066-.325.122-.91.158-1.84l.006-.122h-1.498M33.404 13.357c.49-1.399.048-2.55-.479-3.11 0 0-.007-.002-.011-.006-1.477-1.33-6.2-3.584-14.51-3.584-1.914 0-3.938.119-6.02.353h-.012c-.016.005-.113.034-.252.07 2.324-1.426 8.382-4.743 12.795-4.813 4.432-.07 5.356 1.248 5.364 1.26.036.055.1.08.164.07.067-.015.114-.067.126-.129.007-.048.15-1.044-.6-1.907-.102-.125-.226-.236-.35-.338 2.348.422 9.494 2.113 9.33 6.912-.138 4.205-5.063 6.91-8.154 8.195 1.635-1.059 2.325-2.172 2.61-2.973zM10.337 6.61C9.092 7.856 8.91 9.781 9.515 10.8c.029.044.079.069.128.072h.008c.088 0 .161-.07.161-.157 0-.01-.004-.021-.004-.029 0-.222.019-1.831 1.094-2.28.026-.006.038-.02.059-.036.109-.026.29-.062.393-.07.954-.095 3.525-.278 6.441-.278 5.156 0 11.801.539 14.235 3.112-.012.415-.366 3.418-8.301 5.101-3.323.706-6.86 1.393-10.33 1.393-2.862 0-5.487-.46-8.02-1.405-1.692-.973-2.652-2.108-2.847-3.377-.242-1.569.22-3.95 3.813-6.375 3.028-2.05 7.641-3.69 13.722-4.889-4.66 1.566-8.852 4.15-9.73 5.028zm24.914-4.56C32.161 1.08 29.04.807 29.007.795c-.022 0-.045.008-.064.015-.665-.31-1.515-.46-2.542-.46-.464 0-.973.03-1.505.093-.39.045-.795.11-1.214.184-.67.072-6.432.716-12.112 2.487C8.272 4.137 5.634 5.35 3.725 6.707 1.332 8.41.085 10.367 0 12.508v.008c.118 2.686 2.766 4.708 7.673 5.85 3.67.86 7.351.934 7.392.934h.002c.054 0 .099-.022.13-.063.704.045 1.417.065 2.143.065 1.656 0 3.374-.12 5.11-.358 2.491-.34 4.426-.846 5.931-1.423 0 0 .015-.004.028-.004.03-.004 3.315-.75 6.554-2.315 4.328-2.083 6.609-4.681 6.609-7.5 0-2.4-2.188-4.356-6.32-5.653z">
                </path>
              </svg>
              <div v-else-if="customLogo" class="d-sm-block d--md-none mx-auto">
                <img :src="customLogo" class="Smartmatic-logo" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-show="showToast" class="row">
        <div class="col">
          <smtt-toast ref="sessionExpiredToast"></smtt-toast>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoginForm from './LoginForm';
import LanguageButton from './LanguageButton';
import { mapActions } from "vuex";
import store from "@/store/store";
import base64ToBlob from "@/utils/base64ToBlob";
export default {
  'name': 'LoginPage',
  'props': {
    'emailSuccess': {
      'default': false
    }
  },
  data() {
    return {
      showToast: false,
      title: null,
      date: null,
      stringDate: null,
      customLogo: null,
      showDefaultLogo: true,
    }
  },
  'methods': {
    ...mapActions([
        'getLoginSettings'
    ]),
    forgotPassword(e) {
      e.preventDefault()
      this.$router.push({ 'name': 'ForgotPassword' })
    },
    onChangeLocale() {
      if (this.date) {
        this.stringDate = this.date.toLocaleDateString(store.state.currentLocale);
      }
    }
  },
  'components': { LoginForm, LanguageButton },
  async mounted() {
    if (window.Intercom) {
      window.Intercom('update',{
        "hide_default_launcher": true
      });
    }

    const toastElement = this.$refs.sessionExpiredToast;
    if (this.$route.query?.expired) {
      this.showToast = true;
      toastElement.error({
        'message': this.$t('error.default.session.expired.message'),
        'header': this.$t('error.default.session.expired.title'),
        'duration': 5000
      });
    } else if (this.$route.query?.failedAutologin) {
      this.showToast = true;
      toastElement.error({
        'message': this.$t('error.default.session.tokenFailed.message'),
        'header': this.$t('error.default.session.tokenFailed.header'),
        'duration': 5000
      });
    } else {
      this.showToast = false;
    }

    if (this.emailSuccess === true) {
      this.$refs.passwordRecoverySuccess.isVisible = true
    }

    try {
      const response = await this.getLoginSettings();
      if (response.success) {
        // When there's no content in the response, we show the default logo
        if (Object.keys(response).length === 1) {
          this.showDefaultLogo = true;
        } else {
          this.showDefaultLogo = false;
        }

        if (response.image) {
          const url = URL.createObjectURL(base64ToBlob(response.image));
          this.$refs.backgroundContainer.style.backgroundImage += `radial-gradient(circle at 51% -13%,rgba(0,175,219,.7),rgba(45,13,131,.9) 78%),url(${url})`;
        }
        this.title = response.title || this.$t('login.title.label');
        if (response.date) {
          this.date = new Date(response.date);
          this.stringDate = this.date.toLocaleDateString(store.state.currentLocale);
        }
        if (response.logoImage) {
          this.customLogo = URL.createObjectURL(base64ToBlob(response.logoImage));
        }
      } else {
        console.error(
            response.message.code
                ? this.$t(response.message.code, response.message.params)
                : response.message
        );
      }
    } catch (error) {
      console.error(error);
    }
  }
}
</script>

<style scoped>
.Forgot-password {
  font-family: Roboto;
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  text-decoration: underline;
  line-height: 1.5;
  letter-spacing: normal;
  color: #212529;
}

.main-page {
  background-blend-mode: multiply;
  min-height: 100vh;
  width: 100%;
  background-image: radial-gradient(circle at 51% -13%,rgba(0,175,219,.7),rgba(45,13,131,.9) 78%);
  background-size: cover;
  background-position: center;
}

@media screen and (max-width: 767px) {
  .main-page {
    background-image: none !important;
    min-height: 10vh;
    width: 476px;
  }

  .county-general, .additional-info, .body-1 {
    color: #3d3d3d !important;
  }

  .smt-logo {
    display: none;
  }
}

.additional-info-container {
  margin: 48px 47px 0 48px !important;
}

.smt-logo {
  position: absolute;
  bottom: 36px;
  margin-left: auto;
  margin-right: auto;
  left: 0;
  right: 0;
  text-align: center;
}

.county-general {
  text-align: center;
  font-family: Roboto;
  font-weight: 700!important;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: -.5px;
  color: #fff;
}

.change-lang {
  position: absolute;
  top: 1rem;
  right: 1rem;
  margin-bottom: 1rem;
}

.login-form {
  border-radius: 8px;
  width: 415px !important;
  padding: var(--spacing-08);
}

.additional-info {
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  color: #fff;
}

.text {
  font-weight: 500;
  line-height: 1.33;
}

.date {
  font-weight: 700;
  line-height: 1.5;
  background: none;
  color: #fff;
}

.body-1 {
  font-size: 1rem;
  line-height: 1.5rem;
}
</style>
